// @flow
import moment from 'moment'
import {
  DATE_FORMAT_WITH_TIME,
  DATE_FORMAT_WITHOUT_TIME,
} from '../../utils/formatDate'

// Types
export type Day = {
  date?: any,
  past?: boolean,
  empty?: boolean,
  today?: boolean,
  dow?: number,
  dayNum?: number,
}
export type DayOfWeek = {
  name: string,
}
export type Month = {
  name: string,
  dows: Array<DayOfWeek>,
  days: Array<Array<Day>>,
}

/**
 * By given prev date and selected date create new date that have
 * the same date as in selectedDate and the same time as in prevDate
 * @param  {Date} prevDate
 * @param  {Date} selectedDate
 * @return {Date}
 */
export const getDateFromDateValue = (prevDate: any, selectedDate: any) => {
  const momDate = moment(prevDate || undefined)
  const newDate = moment(selectedDate || undefined)
    .hours(momDate.hours())
    .minutes(momDate.minutes())
  return newDate
}

/**
 * Returns true if given value is defined and it is the same date
 * as provided date value
 * @param  {Any} value
 * @param  {Date} date
 * @return {Boolean}
 */
export const isDateSameDay = (value: any, date: any) =>
  !!value && moment(value).isSame(date, 'day')

/**
 * Returns true if given value is defined and it is the same month
 * as provided date value
 * @param  {Any} value
 * @param  {Date} date
 * @return {Boolean}
 */
export const isDateSameMonth = (value: any, date: any) =>
  !!value && moment(value).isSame(date, 'month')

/**
 * Returns an array of day of the week names
 * @return {Array<{ name: string }>}
 */
export const getDaysOfWeek = () => {
  const dows: Array<DayOfWeek> = new Array(7)
  const weekGenDate = moment()

  for (let i = 1; i <= 7; i++) {
    dows[i - 1] = {
      name: weekGenDate.isoWeekday(i).format('ddd'),
    }
  }

  return dows
}

/**
 * Create an array of months and days to render in component based
 * on given currently selected date value.
 * @return {Array}
 */
export const getDateVariants = (value: any, minDate: any, maxDate: any) => {
  const nowDate = moment(minDate || new Date())
  const selectedDate = moment(value || undefined)
  const currMonth = nowDate.month()
  const months: Array<Month> = new Array(6)

  // Go throuth 6 next months
  selectedDate.date(1).add(-1, 'month')
  for (let j = currMonth; j < currMonth + 6; j++) {
    const currMonthDate = selectedDate.date(1).add(1, 'month')
    months[j - currMonth] = getMonthData(currMonthDate, minDate, maxDate)
  }

  return months
}

/**
 * Create data for one month based on given active value (date)
 * and min/max dates. Returns data structure suitable to render
 * a month
 * @param  {Date} value
 * @param  {Date} minDate
 * @param  {Date} maxDate
 * @return {Object}
 */
export const getMonthData = (
  value: any,
  minDate: moment = moment(new Date()),
  maxDate: any
): Month => {
  const nowDate = moment(new Date())
  const currMonthDate = moment(value || undefined).date(1)
  const days: Array<Day> = new Array(currMonthDate.daysInMonth())

  // Add all days in month
  for (let k = 1; k <= currMonthDate.daysInMonth(); k++) {
    const dayDate = currMonthDate.date(k)
    days[k - 1] = {
      dayNum: k,
      date: dayDate.clone(),
      dow: dayDate.isoWeekday(),
      today: dayDate.isSame(nowDate, 'day'),
      past:
        dayDate.isBefore(minDate, 'day') ||
        (maxDate && dayDate.isAfter(maxDate, 'day')),
    }
  }

  // Fill days to monday before first day of month to make the grid
  const firstDate = days[0]
  if (firstDate && typeof firstDate.dow !== 'undefined' && firstDate.dow > 1) {
    for (let l = 1; l < firstDate.dow; l++) {
      days.unshift({ empty: true })
    }
  }

  // Fill days to saturday after last day of month to make the grid
  const lastDate = last(days)
  if (lastDate && typeof lastDate.dow !== 'undefined' && lastDate.dow < 7) {
    for (let m = lastDate.dow + 1; m <= 7; m++) {
      days.push({ empty: true })
    }
  }

  // Split days by rows with 7 elements
  const rowedDates = days.reduce(
    (accum, day, n) => {
      const currRow = last(accum)
      currRow && currRow.push(day)
      if ((n + 1) % 7 === 0) {
        accum.push([])
      }
      return accum
    },
    [[]]
  )
  rowedDates.pop()

  // set locale before formatting because date can be moment object created with previous locale
  return {
    name: currMonthDate.locale(moment.locale()).format('MMMM YYYY'),
    days: rowedDates,
    dows: getDaysOfWeek(),
  }
}

/**
 * Returns last element of given array
 * @param  {Array<any>} array
 * @return {any}
 */
export const last = <T>(array: Array<T>): ?T => array[array.length - 1]

/**
 * Creat final date format based on provided arguments
 * @param  {boolean} disableTime
 * @param  {string} customFormat
 * @return {string}
 */
export const getDateFormat = (disableTime: boolean, customFormat?: string) => {
  const systemFormat = disableTime
    ? DATE_FORMAT_WITHOUT_TIME
    : DATE_FORMAT_WITH_TIME
  return customFormat || systemFormat
}
